import { inject, Injectable } from "@angular/core";
import { IBaseCard, IBaseT1Card, IFirmTaxprepT1Card, isIFirmTaxprepT1Card, isTaxprepT1Card, SaveResultEnum } from "@app/views-interfaces";
import { isJobsBillingCard, JobsBillingCard } from "@app/views-interfaces/jobs-billing-card.interface";
import { JobsAndBillingJobsService } from "@shared/jobs-and-billing-jobs-service/jobs-and-billing-jobs.service";
import { concatMap, finalize } from "rxjs";
import { CardDisplayService, CardUpdateContext } from "../../shared/card-display/card-display.service";
import { UpdateCardResponse } from "@app/views-interfaces/card.interface";
import { CardUpdateRequest, cardRequestExcludedFields } from "@app/views-interfaces/card-update-request";
import { isiFirmTaxDashboard, isJobsBillingDashboard } from "../../views-interfaces/dashboard-model.interface";
import { AnalyticsService, getAnalyticsExtrasFromCard } from "../analytics-service/analytics.service";
import { AuthService } from "../auth";
import { CardService } from "../card-service/card.service";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import { HelpersService } from "../helpers-service/helpers.service";
import {
    IFirmContactInformationService,
    IFirmContactInformationUpdate,
} from "../ifirm-contact-information-service/ifirm-contact-information.service";
import { IFirmRolesService } from "../ifirm-roles-service/ifirm-roles.service";
import { DashboardUpdateMetadata, IfirmTaxDocumentService } from "../ifirm-tax-document-service/ifirm-tax-document.service";
import { IJobsJobModel } from "../interfaces/jobs-job-model.interface";
import { MappingOfStatusesService } from "../mapping-of-statuses-service/mapping-of-statuses.service";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";
import { UsersService } from "../users-service/users.service";
import { UtilsService } from "../utils/utils.service";

export interface QuietChange {
    field: "quiet";
    value: string;
}

export interface ReturnStatusChange {
    field: "returnStatus";
    value: string;
}

export interface JobStatusChange {
    field: "jobStatus";
    value: string;
}

export interface JobCurrentlyResponsibleChange {
    field: "currentlyResponsible";
    value: string;
}

export interface RolesChange {
    field: "roles";
    value: { roleId: string; userId: string };
}

export type CardChange = QuietChange | ReturnStatusChange | JobStatusChange | RolesChange | JobCurrentlyResponsibleChange;

export interface SaveChangesOptions {
    applyReturnStatusToAll: boolean;
    applyAssigneesToAll: boolean;
}

enum eErrorType {
    iFirmTaxprepError,
    iFirmContactsError,
    jobsBillingError,
    trackingError,
}
@Injectable({
    providedIn: "root",
})
export class BusinessLogicService {
    private readonly authService = inject(AuthService);
    private readonly cardService = inject(CardService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly helpersService = inject(HelpersService);
    private readonly ifirmTaxDocumentService = inject(IfirmTaxDocumentService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly responseErrorHandler = inject(ResponseErrorHandlerService);
    private readonly iFirmRolesService = inject(IFirmRolesService);
    private readonly usersService = inject(UsersService);
    private readonly iFirmContactInformationService = inject(IFirmContactInformationService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly jobsAndBillingJobsService = inject(JobsAndBillingJobsService);

    private readonly sortByStatusDate: (a: { statusDate: number }, b: { statusDate: number }) => number = (a, b) => a.statusDate - b.statusDate;

    constructor() {}

    /**
     * Handles a set of changes to be applied on a card, including integrations with other products.
     * This will compute changes without modifying the original cards, detect if we need to apply changes to members, and
     * send the update request to Tracking and iFirm Taxprep.
     * @param card The original card to be modified
     * @param changes The set of changes to apply on the provided card
     */
    async saveChanges(card: IBaseCard, changes: CardChange[], { applyReturnStatusToAll, applyAssigneesToAll }: SaveChangesOptions) {
        this.helpersService.spinnerRequestedEmitter.next(true);
        const updateContext = this.cardDisplayService.captureUpdateContext();
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        const useiFirmUserId = this.dashboardsService.useiFirmUserId(currentDashboard);
        const allApplicableChanges = this.getAllApplicableChanges(card, changes, applyReturnStatusToAll, applyAssigneesToAll);

        const cardsToSave = allApplicableChanges.map(({ card, changes }) => this.getChangedCard(card, changes, useiFirmUserId));

        let jobsSaveResult = SaveResultEnum.NotApplicable;
        let iFirmTaxprepSaveResult = SaveResultEnum.NotApplicable;
        let contactSaveResult = SaveResultEnum.NotApplicable;

        if (isJobsBillingDashboard(currentDashboard)) {
            jobsSaveResult = isJobsBillingCard(card) ? await this.saveJobsBillingChanges(card, changes) : SaveResultEnum.NotApplicable;
        } else if (isiFirmTaxDashboard(currentDashboard)) {
            const iFirmTaxprepChanges = allApplicableChanges
                .map(({ card, changes }) => this.getiFirmTaxChanges(card, changes))
                .filter((change) => change != undefined);

            const contactsChanges = allApplicableChanges
                .map(({ card, changes }) => this.getContactChanges(card, changes))
                .filter((change) => change != undefined);

            if (iFirmTaxprepChanges.length > 0) {
                try {
                    const response = await this.ifirmTaxDocumentService.updateDocument(iFirmTaxprepChanges);
                    if (response && response.length > 0) {
                        iFirmTaxprepSaveResult = SaveResultEnum.Failed;
                    } else {
                        iFirmTaxprepSaveResult = SaveResultEnum.Success;
                    }
                } catch {
                    iFirmTaxprepSaveResult = SaveResultEnum.Failed;
                }
            }

            if (iFirmTaxprepSaveResult != SaveResultEnum.Failed && contactsChanges.length > 0) {
                try {
                    await this.iFirmContactInformationService.updateContacts(contactsChanges);
                    contactSaveResult = SaveResultEnum.Success;
                } catch {
                    contactSaveResult = SaveResultEnum.Failed;
                }
            }
        }

        if (iFirmTaxprepSaveResult == SaveResultEnum.Failed) {
            this.handleError(eErrorType.iFirmTaxprepError, true);
        } else if (contactSaveResult == SaveResultEnum.Failed) {
            this.handleError(eErrorType.iFirmContactsError, true);
        } else if (jobsSaveResult == SaveResultEnum.Failed) {
            this.handleError(eErrorType.jobsBillingError, false);
        } else {
            const hasIFirmChanges =
                iFirmTaxprepSaveResult != SaveResultEnum.NotApplicable ||
                contactSaveResult != SaveResultEnum.NotApplicable ||
                jobsSaveResult != SaveResultEnum.NotApplicable;
            for (const card of cardsToSave) {
                this.updateCard(card, hasIFirmChanges, updateContext);
            }
        }
        if (
            iFirmTaxprepSaveResult == SaveResultEnum.Failed ||
            contactSaveResult == SaveResultEnum.Failed ||
            jobsSaveResult == SaveResultEnum.Failed
        ) {
            this.helpersService.spinnerRequestedEmitter.next(false);
        }
        if (isJobsBillingDashboard(currentDashboard)) {
            this.trackJobsBillingCardSaveAnalytics(card as JobsBillingCard, changes);
        } else {
            this.trackiFirmTaxprepCardSaveAnalytics(card as IBaseT1Card, changes);
        }
    }

    private async saveJobsBillingChanges(card: JobsBillingCard, changes: CardChange[]): Promise<SaveResultEnum> {
        const statusChange = changes.find((c): c is JobStatusChange => c.field === "jobStatus");
        const currentlyResponsibleChange = changes.find((c): c is JobCurrentlyResponsibleChange => c.field === "currentlyResponsible");
        if (!statusChange && !currentlyResponsibleChange) return SaveResultEnum.NotApplicable;
        try {
            const payload: IJobsJobModel = {
                id: Number(card.job.id),
                ...(statusChange && { jobStatusId: statusChange.value }),
                ...(currentlyResponsibleChange && { currentlyResponsible: currentlyResponsibleChange.value }),
            };
            const response = await this.jobsAndBillingJobsService.updateJob(card.job.id, payload);
            if (response && response.length > 0) {
                return SaveResultEnum.Failed;
            }
            return SaveResultEnum.Success;
        } catch (err) {
            console.error("jobsAndBillingJobsService.updateJob error: " + err);
            return SaveResultEnum.Failed;
        }
    }

    private trackiFirmTaxprepCardSaveAnalytics(card: IBaseT1Card, changes: CardChange[]): void {
        const returnStatusChanged = changes.some((c) => c.field === "returnStatus");
        const assigneeChanged = changes.some((c) => c.field === "roles");
        const extra = getAnalyticsExtrasFromCard(card);
        this.analyticsService.track("Saved a card - iFirm Taxprep & Taxprep", {
            ...extra,
            assigneeChanged: assigneeChanged ? "Yes" : "No",
            groupChanged: "No",
            return_statusChanged: returnStatusChanged ? "Yes" : "No",
            temporary_noteChanged: "No",
            permanent_noteChanged: "No",
            apply_to_all_return_status: "No",
            apply_to_all_assignee: "No",
            delivery_methodChanged: "No",
            is_vipChanged: "No",
            snoozeChanged: "No",
            fromDragAndDrop: "Yes",
            wasUnassigned: "No",
            DeadlineChanged: "No",
            Date_ReceivedChanged: "No",
        });
    }

    private trackJobsBillingCardSaveAnalytics(card: JobsBillingCard, changes: CardChange[]): void {
        const jobStatusChanged = changes.some((c) => c.field === "jobStatus");
        this.analyticsService.track("Saved a card - Jobs & Billing", {
            cardId: card.cardId,
            ctx_jobId: card.job?.id,
            target_start_dateChanged: "No",
            target_end_dateChanged: "No",
            date_receivedChanged: "No",
            job_statusChanged: jobStatusChanged ? "Yes" : "No",
            priorityChanged: "No",
            tracking_NotesAdded: "No",
            contact_group1Changed: "No",
            contact_group2Changed: "No",
            contact_partnerChanged: "No",
            contact_managerChanged: "No",
            currently_responsibleChanged: "No", // TODO DASH-21246: populate once assignees drag-and-drop is implemented for J&B
            job_partnerChanged: "No",
            job_managerChanged: "No",
            job_ownerChanged: "No",
            job_group1Changed: "No",
            job_group2Changed: "No",
            snoozeChanged: "No",
            fromDragAndDrop: "Yes",
            wasUnassigned: "No", // TODO DASH-21246: populate once assignees drag-and-drop is implemented for J&B
        });
    }

    /**
     * From a specific card, returns a list of cards and all applicable changes for each. This method always includes
     * the initial card, plus members if there are changes that are applicable to them as well.
     * @param card
     * @param changes
     * @returns
     */
    getAllApplicableChanges(card: IBaseCard, changes: CardChange[], applyReturnStatusToAll: boolean, applyAssigneesToAll: boolean) {
        const allChanges: { card: any; changes: CardChange[] }[] = [{ card, changes }];

        const propagateReturnStatus = applyReturnStatusToAll && changes.some((change) => change.field === "returnStatus");
        const propagateAssignee = applyAssigneesToAll && changes.some((change) => change.field === "roles");

        if ((isTaxprepT1Card(card) || isIFirmTaxprepT1Card(card)) && (propagateReturnStatus || propagateAssignee)) {
            const memberCards =
                card.members?.map((member) => this.cardDisplayService.getCardById(member.cardId)).filter((member) => member != undefined) ?? [];
            for (const member of memberCards) {
                allChanges.push({
                    card: member,
                    changes: changes.filter((change) => {
                        return (applyReturnStatusToAll && change.field === "returnStatus") || (applyAssigneesToAll && change.field === "roles");
                    }),
                });
            }
        }

        return allChanges;
    }

    /**
     * Copies the card and applies changes on the copy including validations and specific business logic related with each changes.
     * @param card
     * @param changes
     */
    getChangedCard(card: IBaseCard, changes: CardChange[], useiFirmUserId: boolean) {
        const newCard = UtilsService.recursiveDeepCopy(card);
        for (const change of changes) {
            switch (change.field) {
                case "jobStatus":
                    if (isJobsBillingCard(newCard) && newCard.job.statusId !== change.value) {
                        this.setJobStatus(newCard, change.value, this.authService.currentUser?.ifirm_userid);
                    }
                    break;
                case "currentlyResponsible":
                    if (isJobsBillingCard(newCard) && newCard.job.currentlyResponsible !== change.value) {
                        this.setCurrentlyResponsible(newCard, change);
                    }
                    break;
                case "returnStatus":
                    if (!isJobsBillingCard(newCard) && newCard.return.statusId !== change.value) {
                        this.setReturnStatus(
                            newCard,
                            change.value,
                            this.authService.currentUser.sub,
                            useiFirmUserId,
                            changes.some((change) => change.field === "quiet"),
                        );
                    }
                    break;
                case "roles":
                    this.setRoleUserMappings(newCard, change.value);
                    break;
            }
            // TODO migrate card change logic here eventually.
        }
        return newCard;
    }

    setJobStatus(card: JobsBillingCard, newStatusId: string, userId: string, timestamp = Date.now()) {
        const statusDate = Math.floor(this.helpersService.convertMillisecondsToSeconds(timestamp));
        card.job.statusId = newStatusId;
        card.job.statusDate = statusDate;
        card.job.statusUserId = userId;
        card.statusesHistory.push({
            statusId: newStatusId,
            statusDate,
            userId,
        });
        card.statusesHistory.sort(this.sortByStatusDate);
    }

    private setCurrentlyResponsible(card: JobsBillingCard, change: JobCurrentlyResponsibleChange) {
        card.job.currentlyResponsible = change.value;
    }

    /**
     * Change the return status ID set in the card, adds an entry in the return status history, and resets quiet field if the card has changed step.
     * @param card
     * @param newStatusId
     * @param userId
     * @param timestamp
     */
    setReturnStatus(card: IBaseT1Card, newStatusId, userId, useiFirmUserId: boolean, hasQuietChange: boolean, timestamp = Date.now()) {
        const originalStatusId = card.return.statusId;

        if (useiFirmUserId) {
            userId = this.usersService.getUserById(this.helpersService.getCurrentUser().sub)?.IFirmUserId ?? userId;
        }

        card.return.statusId = newStatusId;
        card.return.statusDate = Math.floor(this.helpersService.convertMillisecondsToSeconds(timestamp));
        card.return.statusUserId = userId;
        card.statusesHistory.push({
            statusId: newStatusId,
            statusDate: Math.floor(this.helpersService.convertMillisecondsToSeconds(timestamp)),
            userId: userId,
        });
        card.statusesHistory.sort(this.sortByStatusDate);

        if (!hasQuietChange) {
            // Changing steps resets the alerts : clear the snooze value.
            const oldStep = this.mappingOfStatusesService.getStepFromStatusId(originalStatusId);
            const newStep = this.mappingOfStatusesService.getStepFromStatusId(newStatusId);
            if (oldStep?.Id !== newStep?.Id) {
                card.quiet = null;
            }
        }
    }

    /**
     * Either create a roleId/userId pair or update the userId in an existing entry in the card's roleUserMapping.
     * @param card
     * @param changes
     */
    setRoleUserMappings(card: IBaseT1Card, value) {
        const found = card.roleUserMapping.find((mapping) => mapping.roleId == value.roleId);
        if (found) {
            found.userId = value.userId;
        } else {
            card.roleUserMapping.push(value);
        }
    }

    /**
     * Returns the payload to send to iFirm Taxprep for a card and the given set of changes.
     * @param card
     * @param changes
     * @returns
     */
    getiFirmTaxChanges(card: IFirmTaxprepT1Card, changes: CardChange[]) {
        let metadata: DashboardUpdateMetadata;

        const returnChange = changes.find((change) => change.field === "returnStatus");
        if (returnChange && card.return.statusId != returnChange.value) {
            metadata = {
                documentMetadata: {
                    documentId: card.documentId,
                    returnId: card.return.id,
                },
            };
            metadata.documentMetadata.status = returnChange.value;
        }

        const rolesChanges = changes.filter((change) => change.field === "roles" && this.hasiFirmTaxprepRoleChanges(card, change.value));
        if (rolesChanges.length > 0) {
            if (!metadata) {
                metadata = {
                    documentMetadata: {
                        documentId: card.documentId,
                        returnId: card.return.id,
                    },
                };
            }
            this.setiFirmTaxRolesChange(metadata, rolesChanges);
        }

        return metadata;
    }

    getContactChanges(card, changes: CardChange[]) {
        let contactPayload: IFirmContactInformationUpdate;

        const rolesChanges = changes.filter((change) => change.field === "roles" && this.hasContactRoleChanges(card, change.value));
        if (rolesChanges.length > 0) {
            contactPayload = {
                contactId: Number(card.contactInformation.iFirmContactId),
                payload: {},
                deliveryMethod: null,
            };
            this.setContactRolesChange(contactPayload, rolesChanges);
        }

        return contactPayload;
    }

    /**
     * Set in the iFirm Taxprep payload the changes in roles for preparer and reviewer
     * @param metadata
     * @param changes
     */
    setiFirmTaxRolesChange(metadata: DashboardUpdateMetadata, changes: CardChange[]) {
        for (const change of changes) {
            if (change.field === "roles") {
                const { roleId, userId } = change.value as { roleId: string; userId: string };
                const iFirmUserId = this.usersService.getUserById(userId)?.IFirmUserId;
                metadata.documentMetadata.assignees ??= {};
                if (roleId === this.iFirmRolesService.getPreparer().Id) {
                    metadata.documentMetadata.assignees.preparerUserId = iFirmUserId || "-1";
                } else if (roleId === this.iFirmRolesService.getReviewer().Id) {
                    metadata.documentMetadata.assignees.reviewerUserId = iFirmUserId || "-1";
                }
            }
        }
    }

    /**
     * Set in the Contact payload the changes in roles for partner and manager
     * @param contactPayload
     * @param changes
     */
    setContactRolesChange(contactPayload: IFirmContactInformationUpdate, changes: CardChange[]) {
        for (const change of changes) {
            if (change.field === "roles") {
                const { roleId, userId } = change.value as { roleId: string; userId: string };
                const ifirmUserId = this.usersService.getUserById(userId)?.IFirmUserId;
                if (roleId === this.iFirmRolesService.getManager().Id) {
                    contactPayload.payload["manager_id"] = Number(ifirmUserId);
                } else if (roleId === this.iFirmRolesService.getPartner().Id) {
                    contactPayload.payload["client_of"] = Number(ifirmUserId);
                }
            }
        }
    }

    /**
     * Return true if the changed value is different from the card's value, and is for preparer or reviewer
     * @param card
     * @param value
     * @returns
     */
    hasiFirmTaxprepRoleChanges(card, value) {
        return (
            !card.roleUserMapping.some((mapping) => mapping.roleId === value.roleId && mapping.userId === value.userId) &&
            (value.roleId === this.iFirmRolesService.getPreparer().Id || value.roleId === this.iFirmRolesService.getReviewer().Id)
        );
    }

    /**
     * Return true if the changed value is different from the card's value, and is for partner or manager
     * @param card
     * @param value
     * @returns
     */
    hasContactRoleChanges(card, value) {
        return (
            !card.roleUserMapping.some((mapping) => mapping.roleId === value.roleId && mapping.userId === value.userId) &&
            (value.roleId === this.iFirmRolesService.getManager().Id || value.roleId === this.iFirmRolesService.getPartner().Id)
        );
    }

    removeCardFields<T extends object>(cardData: T): CardUpdateRequest<T> {
        const request: CardUpdateRequest<T> = UtilsService.recursiveDeepCopy(cardData);
        for (const field of cardRequestExcludedFields) {
            Reflect.deleteProperty(request, field);
        }
        return request;
    }

    updateCard(card: IBaseCard, hasIFirmChanges: boolean, context = this.cardDisplayService.captureUpdateContext()) {
        if (!this.cardDisplayService.isUpdateContextCurrent(context)) {
            this.helpersService.spinnerRequestedEmitter.next(false);
            this.handleError(eErrorType.trackingError, hasIFirmChanges, new Error("Dashboard changed before the card could be saved."));
            return;
        }
        const localCard = this.cardDisplayService.storeCard(card, false);
        const request = this.removeCardFields(card);
        this.cardService
            .updateCard(card.cardId, { card: request })
            .pipe(
                concatMap((result) => this.handleCardUpdated(card, result, hasIFirmChanges, context)),
                finalize(() => {
                    this.helpersService.spinnerRequestedEmitter.next(false);
                }),
            )
            .subscribe({
                error: (error) => {
                    this.cardDisplayService.discardLocalCardUpdate(card.cardId, localCard);
                    this.handleError(eErrorType.trackingError, hasIFirmChanges, error);
                },
            });
    }

    async handleCardUpdated(
        card: Pick<IBaseCard, "cardId">,
        result: UpdateCardResponse,
        _hasIFirmChanges: boolean,
        context: CardUpdateContext = this.cardDisplayService.captureUpdateContext(),
    ): Promise<void> {
        await this.cardDisplayService.applySavedCard(card.cardId, result, context);
    }

    handleError(errorType: eErrorType, hasIFirmChanges: boolean, error?) {
        console.error("Card save error", error);
        switch (errorType) {
            case eErrorType.iFirmTaxprepError:
                this.responseErrorHandler.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "UpdateIFirmTaxprepError",
                });
                break;
            case eErrorType.iFirmContactsError:
                this.responseErrorHandler.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "UpdateContactsError",
                });
                break;
            case eErrorType.jobsBillingError:
                this.responseErrorHandler.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "UpdateJobError",
                });
                break;
            case eErrorType.trackingError:
                this.responseErrorHandler.showPopup({
                    typeLayout: "ERROR",
                    messageCode: hasIFirmChanges ? "UpdateTrackingWithIFirmCardError" : "UpdateTrackingCardError",
                });
                break;
        }
        // TODO need to track in sentry?
    }
}
